import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useDarkMode } from '../context/DarkModeContext';
import { 
  ArrowLeft, 
  Save, 
  Camera, 
  User, 
  Mail, 
  Phone, 
  Shield,
  Palette,
  Bell,
  Eye,
  EyeOff,
  Award,
  MessageSquare,
  Target,
  CheckCircle
} from 'lucide-react';
import Cropper from 'react-easy-crop';
import Slider from '@mui/material/Slider';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import getCroppedImg from '../utils/cropImage';
import api from '../services/api';

const EditProfile = () => {
  const navigate = useNavigate();
  const { user, loading, updateProfile, updatePreferences, updatePassword, updateProfileImage, logout } = useUser();
  const { isDarkMode, setIsDarkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);

  // Profile form state - initialize with current user data
  const [profileForm, setProfileForm] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    bio: '',
    location: '',
    phone: '',
    website: '',
    twitter: '',
    linkedin: ''
  });

  // Preferences form state - initialize with current user preferences
  const [preferencesForm, setPreferencesForm] = useState({
    theme: 'light',
    debateFormats: ['Lincoln-Douglas'],
    difficultyPreference: 'intermediate',
    timezone: 'UTC',
    language: 'en',
    emailNotifications: true,
    tournamentReminders: true,
    weeklyReports: true,
    achievementAlerts: true,
    debateInvites: true,
    profileVisibility: 'public',
    showEmail: false,
    showPhone: false,
    allowMessages: true
  });

  // Security form state
  const [securityForm, setSecurityForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Update form state when user changes
  useEffect(() => {
    if (user) {
      setProfileForm(prev => ({
        ...prev,
        username: user?.username || '',
        email: user?.email || '',
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        bio: user?.bio || '',
        location: user?.location || '',
        phone: user?.phone || '',
        website: user?.website || '',
        twitter: user?.twitter || '',
        linkedin: user?.linkedin || ''
      }));
      
      setPreferencesForm(prev => ({
        ...prev,
        theme: user?.preferences?.theme || 'light',
        debateFormats: user?.preferences?.debateFormats || ['Lincoln-Douglas'],
        difficultyPreference: user?.preferences?.difficultyPreference || 'intermediate',
        timezone: user?.preferences?.timezone || 'UTC',
        language: user?.preferences?.language || 'en',
        emailNotifications: user?.preferences?.emailNotifications || true,
        tournamentReminders: user?.preferences?.tournamentReminders || true,
        weeklyReports: user?.preferences?.weeklyReports || true,
        achievementAlerts: user?.preferences?.achievementAlerts || true,
        debateInvites: user?.preferences?.debateInvites || true,
        profileVisibility: user?.preferences?.profileVisibility || 'public',
        showEmail: user?.preferences?.showEmail || false,
        showPhone: user?.preferences?.showPhone || false,
        allowMessages: user?.preferences?.allowMessages || true
      }));

      // Set preview URL for existing profile image
      if (user?.profileImage) {
        setPreviewUrl(user.profileImage);
      } else {
        setPreviewUrl(null);
      }
    }
  }, [user]);

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
        setCropModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropSave = async () => {
    setIsLoading(true);
    try {
      const croppedImage = await getCroppedImg(previewUrl, croppedAreaPixels, rotation);
      await updateProfileImage(croppedImage);
      setCropModalOpen(false);
      setSelectedFile(null);
      setPreviewUrl(croppedImage);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      alert('Error saving cropped image: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle remove profile image
  const handleRemoveProfileImage = async () => {
    if (!user?.profileImage) {
      return;
    }

    if (!window.confirm('Are you sure you want to remove your profile picture?')) {
      return;
    }

    setIsLoading(true);
    try {
      await updateProfileImage(null);
      setPreviewUrl(null);
      setSelectedFile(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      alert('Error removing profile image: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Get user initials for profile image
  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    } else if (user?.username) {
      return user.username.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Show loading only if we're actually loading and there's a token
  if (loading && localStorage.getItem('token')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If no user and no token, redirect to login
  if (!user) {
    navigate('/login', { state: { from: '/profile/edit' } });
    return null;
  }

  const handleProfileChange = (field, value) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePreferencesChange = (field, value) => {
    setPreferencesForm(prev => {
      // If theme is being changed, update dark mode context immediately
      if (field === 'theme') {
        if (value === 'dark') setIsDarkMode(true);
        else if (value === 'light') setIsDarkMode(false);
        else if (value === 'auto') setIsDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
      }
      return {
      ...prev,
      [field]: value
      };
    });
  };

  const handleSecurityChange = (field, value) => {
    setSecurityForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    
    try {
      // Update profile data
      await updateProfile({
        username: profileForm.username,
        email: profileForm.email,
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        bio: profileForm.bio,
        location: profileForm.location,
        phone: profileForm.phone,
        website: profileForm.website,
        twitter: profileForm.twitter,
        linkedin: profileForm.linkedin
      });

      // Update preferences
      await updatePreferences(preferencesForm);

      // Show saved message
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      
      // Optional: Show success notification
      console.log('Profile updated successfully!');
      
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);
    
    try {
      // Update profile data
      await updateProfile({
        username: profileForm.username,
        email: profileForm.email,
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        bio: profileForm.bio,
        location: profileForm.location,
        phone: profileForm.phone,
        website: profileForm.website,
        twitter: profileForm.twitter,
        linkedin: profileForm.linkedin
      });

      // Show saved message
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    setIsLoading(true);
    try {
      // Update preferences
      await updatePreferences(preferencesForm);
      // After saving, ensure dark mode context matches saved theme
      if (preferencesForm.theme === 'dark') setIsDarkMode(true);
      else if (preferencesForm.theme === 'light') setIsDarkMode(false);
      else if (preferencesForm.theme === 'auto') setIsDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
      // Show saved message
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error updating preferences:', error);
      alert('Error updating preferences: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSecurity = async () => {
    if (securityForm.newPassword !== securityForm.confirmPassword) {
      alert('New passwords do not match!');
      return;
    }

    if (securityForm.newPassword.length < 8) {
      alert('Password must be at least 8 characters long!');
      return;
    }

    setIsLoading(true);
    
    try {
      // Update password
      await updatePassword({
        currentPassword: securityForm.currentPassword,
        newPassword: securityForm.newPassword
      });
      
      // Clear the form
      setSecurityForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Show saved message
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      
    } catch (error) {
      console.error('Error updating password:', error);
      alert('Error updating password: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const debateFormats = [
    'Lincoln-Douglas',
    'Parliamentary',
    'Policy Debate',
    'Public Forum',
    'IPDA',
    'Model UN',
    'Model Congress'
  ];

  const difficultyLevels = [
    'beginner',
    'intermediate',
    'advanced',
    'expert'
  ];

  const themes = [
    { value: 'light', label: 'Light', description: 'Clean and bright interface' },
    { value: 'dark', label: 'Dark', description: 'Easy on the eyes' },
    { value: 'auto', label: 'Auto', description: 'Follows system preference' }
  ];

  const renderProfileTab = () => (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-6">
      {/* Profile Picture */}
        <div className="flex items-center space-x-6">
          <div className="relative">
            {previewUrl ? (
              <img 
                src={previewUrl} 
                alt="Profile" 
                className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
              />
            ) : (
              <div className="w-24 h-24 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white text-3xl font-bold">
                {getUserInitials()}
              </div>
            )}
            {isLoading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="space-y-3">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="profile-image-input"
              />
              <label 
                htmlFor="profile-image-input"
                className="btn-outline flex items-center justify-center cursor-pointer"
              >
                <Camera className="w-4 h-4 mr-2" />
                {selectedFile ? 'Change Photo' : 'Upload New Photo'}
              </label>
              
              {user?.profileImage && (
                <button 
                  onClick={handleRemoveProfileImage}
                  disabled={isLoading}
                  className="w-full px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                      Removing...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Remove Photo
                    </>
                  )}
                </button>
              )}
              
            <p className="text-sm text-gray-600 dark:text-gray-400">JPG, PNG or GIF. Max size 2MB.</p>
          </div>
        </div>
      </div>

      {/* Basic Information */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
              Username *
            </label>
            <input
              type="text"
              value={profileForm.username}
              onChange={(e) => handleProfileChange('username', e.target.value)}
            className="input-field dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            />
          </div>
          <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
              Email *
            </label>
            <input
              type="email"
              value={profileForm.email}
              onChange={(e) => handleProfileChange('email', e.target.value)}
            className="input-field dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            />
          </div>
          <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
              First Name
            </label>
            <input
              type="text"
              value={profileForm.firstName}
              onChange={(e) => handleProfileChange('firstName', e.target.value)}
            className="input-field dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            />
          </div>
          <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
              Last Name
            </label>
            <input
              type="text"
              value={profileForm.lastName}
              onChange={(e) => handleProfileChange('lastName', e.target.value)}
            className="input-field dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            />
          </div>
          <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={profileForm.phone}
              onChange={(e) => handleProfileChange('phone', e.target.value)}
            className="input-field dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            />
          </div>
          <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
              Location
            </label>
            <input
              type="text"
              value={profileForm.location}
              onChange={(e) => handleProfileChange('location', e.target.value)}
            className="input-field dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            />
          </div>
          <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
              Website
            </label>
            <input
              type="url"
              value={profileForm.website}
              onChange={(e) => handleProfileChange('website', e.target.value)}
            className="input-field dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            />
          </div>
        </div>
        
        <div className="mt-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
            Bio
          </label>
          <textarea
            value={profileForm.bio}
            onChange={(e) => handleProfileChange('bio', e.target.value)}
            rows={4}
          className="input-field dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            placeholder="Tell us about yourself..."
          />
      </div>
    </div>
  );

  const renderPreferencesTab = () => (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-6">
      {/* Debate Preferences */}
        <div className="space-y-6">
          <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-3">
              Preferred Debate Formats
            </label>
            <div className="grid md:grid-cols-2 gap-3">
              {debateFormats.map((format) => (
                <label key={format} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferencesForm.debateFormats.includes(format)}
                    onChange={(e) => handlePreferencesChange('debateFormats', e.target.checked ? [...preferencesForm.debateFormats, format] : preferencesForm.debateFormats.filter(f => f !== format))}
                  className="text-primary-600 mt-1 dark:bg-gray-700 dark:text-gray-100"
                  />
                  <div>
                  <div className="font-medium dark:text-gray-100">{format}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
                Difficulty Preference
              </label>
              <select
                value={preferencesForm.difficultyPreference}
                onChange={(e) => handlePreferencesChange('difficultyPreference', e.target.value)}
              className="input-field dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
              >
                {difficultyLevels.map((level) => (
                  <option key={level} value={level}>{level.charAt(0).toUpperCase() + level.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
                Timezone
              </label>
              <select
                value={preferencesForm.timezone}
                onChange={(e) => handlePreferencesChange('timezone', e.target.value)}
              className="input-field dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
              >
                <option value="EST">Eastern Time (EST)</option>
                <option value="CST">Central Time (CST)</option>
                <option value="MST">Mountain Time (MST)</option>
                <option value="PST">Pacific Time (PST)</option>
                <option value="GMT">Greenwich Mean Time (GMT)</option>
                <option value="CET">Central European Time (CET)</option>
              </select>
          </div>
        </div>
      </div>

      {/* Display Preferences */}
      <div className="space-y-6">
          <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
              Theme
            </label>
            <select
              value={preferencesForm.theme}
              onChange={(e) => handlePreferencesChange('theme', e.target.value)}
            className="input-field dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            >
              {themes.map((theme) => (
                <option key={theme.value} value={theme.value}>{theme.label}</option>
              ))}
            </select>
          </div>
          <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
              Language
            </label>
            <select
              value={preferencesForm.language}
              onChange={(e) => handlePreferencesChange('language', e.target.value)}
            className="input-field dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="zh">Chinese</option>
            </select>
        </div>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-6">
        <div className="space-y-4">
          <label className="flex items-center justify-between cursor-pointer">
            <div className="flex items-center space-x-3">
              <Mail className="w-5 h-5 text-gray-400" />
              <div>
              <div className="font-medium dark:text-gray-100">Email Notifications</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Receive important updates via email</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferencesForm.emailNotifications}
              onChange={(e) => handlePreferencesChange('emailNotifications', e.target.checked)}
            className="text-primary-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </label>
          
          <label className="flex items-center justify-between cursor-pointer">
            <div className="flex items-center space-x-3">
              <Bell className="w-5 h-5 text-gray-400" />
              <div>
              <div className="font-medium dark:text-gray-100">Tournament Reminders</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Get notified about upcoming tournaments</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferencesForm.tournamentReminders}
              onChange={(e) => handlePreferencesChange('tournamentReminders', e.target.checked)}
            className="text-primary-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </label>
          
          <label className="flex items-center justify-between cursor-pointer">
            <div className="flex items-center space-x-3">
              <Target className="w-5 h-5 text-gray-400" />
              <div>
              <div className="font-medium dark:text-gray-100">Weekly Progress Reports</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Receive weekly summaries of your activity</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferencesForm.weeklyReports}
              onChange={(e) => handlePreferencesChange('weeklyReports', e.target.checked)}
            className="text-primary-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </label>
          
          <label className="flex items-center justify-between cursor-pointer">
            <div className="flex items-center space-x-3">
              <Award className="w-5 h-5 text-gray-400" />
              <div>
              <div className="font-medium dark:text-gray-100">Achievement Alerts</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Get notified when you earn achievements</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferencesForm.achievementAlerts}
              onChange={(e) => handlePreferencesChange('achievementAlerts', e.target.checked)}
            className="text-primary-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </label>
          
          <label className="flex items-center justify-between cursor-pointer">
            <div className="flex items-center space-x-3">
              <User className="w-5 h-5 text-gray-400" />
              <div>
              <div className="font-medium dark:text-gray-100">Debate Invites</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Receive invitations to debates</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferencesForm.debateInvites}
              onChange={(e) => handlePreferencesChange('debateInvites', e.target.checked)}
            className="text-primary-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </label>
      </div>
    </div>
  );

  const renderPrivacyTab = () => (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-6">
        <div className="space-y-6">
          <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
              Profile Visibility
            </label>
            <select
              value={preferencesForm.profileVisibility}
              onChange={(e) => handlePreferencesChange('profileVisibility', e.target.value)}
            className="input-field dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            >
              <option value="public">Public - Anyone can view</option>
              <option value="registered">Registered Users Only</option>
              <option value="friends">Friends Only</option>
              <option value="private">Private</option>
            </select>
          </div>
          
          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                <div className="font-medium dark:text-gray-100">Show Email Address</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Display your email on your public profile</div>
                </div>
              </div>
              <input
                type="checkbox"
                checked={preferencesForm.showEmail}
                onChange={(e) => handlePreferencesChange('showEmail', e.target.checked)}
              className="text-primary-600 dark:bg-gray-700 dark:text-gray-100"
              />
            </label>
            
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                <div className="font-medium dark:text-gray-100">Show Phone Number</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Display your phone number on your public profile</div>
                </div>
              </div>
              <input
                type="checkbox"
                checked={preferencesForm.showPhone}
                onChange={(e) => handlePreferencesChange('showPhone', e.target.checked)}
              className="text-primary-600 dark:bg-gray-700 dark:text-gray-100"
              />
            </label>
            
            <label className="flex items-center justify-between cursor-pointer">
              <div className="flex items-center space-x-3">
                <MessageSquare className="w-5 h-5 text-gray-400" />
                <div>
                <div className="font-medium dark:text-gray-100">Allow Direct Messages</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Let other users send you private messages</div>
                </div>
              </div>
              <input
                type="checkbox"
                checked={preferencesForm.allowMessages}
                onChange={(e) => handlePreferencesChange('allowMessages', e.target.checked)}
              className="text-primary-600 dark:bg-gray-700 dark:text-gray-100"
              />
            </label>
        </div>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-6">
        <div className="space-y-4">
          <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={securityForm.currentPassword}
                onChange={(e) => handleSecurityChange('currentPassword', e.target.value)}
              className="input-field pr-10 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
              </button>
            </div>
          </div>
          
          <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
              New Password
            </label>
            <input
              type="password"
              value={securityForm.newPassword}
              onChange={(e) => handleSecurityChange('newPassword', e.target.value)}
            className="input-field dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            />
          </div>
          
          <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-2">
              Confirm New Password
            </label>
            <input
              type="password"
              value={securityForm.confirmPassword}
              onChange={(e) => handleSecurityChange('confirmPassword', e.target.value)}
            className="input-field dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
            />
        </div>
      </div>

      {/* Delete Account Section */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 text-red-800">Delete Account</h3>
        <p className="text-red-700 mb-4">
          Once you delete your account, there is no going back. Please be certain.
        </p>
        <button
          onClick={async () => {
            const confirmed = window.confirm(
              'Are you absolutely sure you want to delete your account? This action cannot be undone and will permanently remove all your data, including:\n\n' +
              '• Your profile and preferences\n' +
              '• Debate history and achievements\n' +
              '• XP, crowns, and trophies\n' +
              '• All associated data\n\n' +
              'Type "DELETE" to confirm:'
            );
            if (confirmed) {
              const userInput = prompt('Please type "DELETE" to confirm account deletion:');
              if (userInput === 'DELETE') {
                try {
                  await api.deleteAccount();
                  logout();
                  alert('Your account has been deleted.');
                  navigate('/');
                } catch (err) {
                  alert('Failed to delete account: ' + (err.message || 'Unknown error'));
                }
              } else {
                alert('Account deletion cancelled.');
              }
            }
          }}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          Delete Account
        </button>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return renderProfileTab();
      case 'preferences':
        return renderPreferencesTab();
      case 'notifications':
        return renderNotificationsTab();
      case 'privacy':
        return renderPrivacyTab();
      case 'security':
        return renderSecurityTab();
      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'} pt-24 pb-8`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/profile')}
            className="btn-outline flex items-center"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Profile
          </button>
          
          {/* Save Status Indicator */}
          {saved && (
            <div className="flex items-center space-x-2 text-green-600 bg-green-50 dark:bg-green-900 px-4 py-2 rounded-lg">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Changes saved successfully!</span>
            </div>
          )}
          
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="btn-primary flex items-center"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save All Changes
              </>
            )}
          </button>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold mb-4">Settings</h3>
              <div className="space-y-2">
                {[
                  { id: 'profile', label: 'Profile', icon: User },
                  { id: 'preferences', label: 'Preferences', icon: Palette },
                  { id: 'notifications', label: 'Notifications', icon: Bell },
                  { id: 'privacy', label: 'Privacy', icon: Shield },
                  { id: 'security', label: 'Security', icon: Shield }
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full text-left p-3 rounded-lg transition-colors flex items-center space-x-3 ${
                        activeTab === tab.id
                          ? 'bg-primary-50 border border-primary-200 text-primary-700 dark:bg-primary-900 dark:text-primary-200'
                          : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
            {renderTabContent()}
            </div>
            
            {/* Tab-specific save buttons */}
            <div className="mt-6 flex justify-end">
              {activeTab === 'profile' && (
                <button
                  onClick={handleSaveProfile}
                  disabled={isLoading}
                  className="btn-primary flex items-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Profile
                    </>
                  )}
                </button>
              )}
              
              {activeTab === 'preferences' && (
                <button
                  onClick={handleSavePreferences}
                  disabled={isLoading}
                  className="btn-primary flex items-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Preferences
                    </>
                  )}
                </button>
              )}
              
              {activeTab === 'notifications' && (
                <button
                  onClick={handleSavePreferences}
                  disabled={isLoading}
                  className="btn-primary flex items-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Notifications
                    </>
                  )}
                </button>
              )}
              
              {activeTab === 'privacy' && (
                <button
                  onClick={handleSavePreferences}
                  disabled={isLoading}
                  className="btn-primary flex items-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Privacy Settings
                    </>
                  )}
                </button>
              )}
              
              {activeTab === 'security' && (
                <button
                  onClick={handleSaveSecurity}
                  disabled={isLoading}
                  className="btn-primary flex items-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Update Password
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <Dialog open={cropModalOpen} onClose={() => setCropModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Crop & Rotate Profile Image</DialogTitle>
        <DialogContent>
          <div style={{ position: 'relative', width: '100%', height: 300, background: '#333' }}>
            <Cropper
              image={previewUrl}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
            />
          </div>
          <div className="flex flex-col gap-4 mt-4">
            <label>Zoom</label>
            <Slider value={zoom} min={1} max={3} step={0.1} onChange={(e, z) => setZoom(z)} />
            <label>Rotation</label>
            <Slider value={rotation} min={0} max={360} step={1} onChange={(e, r) => setRotation(r)} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCropModalOpen(false)} color="secondary">Cancel</Button>
          <Button onClick={handleCropSave} color="primary" disabled={isLoading}>{isLoading ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EditProfile; 